import * as React from 'react';
import { DateTimeSpan } from '@shared/ui-react/UtilComponents';
import { Ajax } from '@shared/Ajax';
import './VehicleInfoCard.scss';
import { cs } from '@shared/utils/utils-classname';
import { MapWrapper } from '@openlayers/MapWrapper';
import { Button } from '@shared/ui-react/Button';
import { StatsStore } from '@pages/trucks/store/StatsStore';
import { FilterStore } from '@pages/trucks/store/FilterStore';
import { MaterialStatsTable } from '@pages/trucks/stats/MaterialStatsTable';
import { observer } from 'mobx-react';
import { DateRangePicker } from '@shared/ui-react/DateRangePicker';
import { DateUtils } from '@shared/utils/DateUtils';
import { Tooltip } from 'primereact/tooltip';
import AttachDocumentsAndLinks from '@shared/attachments/AttachDocumentsAndLinks';
import { EquipmentAttachmentsService } from '@shared/attachments/EquipmentAttachmentsService';
import { Toggle } from '@shared/ui-react/Toggle';
import Feature from 'ol/Feature';
import { VehiclesService } from '@shared/vehicles/VehiclesService';

@observer
export class VehicleInfoCard extends React.Component<
    {
        vehicle: IVehicle;
        mapWrapper: MapWrapper;
        treeData?: ITreeData;
        onLoadActiveModel?: (id: number) => void;
        onModelShowInTree?: (id: number) => void;
        onModelShowInFilesPage?: (id: number) => void;
        onModelShowIn3dPage?: (id: number) => void;
        isTruckPage?: boolean;
        clearLogpointsOnClose?: boolean;
        onLogpointsLoaded?: (ids: number[]) => void;
    },
    {}
> {
    state = {
        // fetched/converted on server
        posE: null,
        posN: null,
        loadedLogpoints: -1,
        loadedLogpointsFeatures: [],
        isLoadingAttachments: false,
        isLoadingTrailAndLogpoints: false,
        fromTimestamp: DateUtils.presets[1].from,
        toTimestamp: null,
        isTrailTruncated: false,
        datePresetIndex: 1,
        emissions: null as IVehicleEmission | null,
    };
    statsFilter: FilterStore = new FilterStore();
    statsStore: StatsStore = new StatsStore(this.statsFilter);

    static defaultProps = {
        clearLogpointsOnClose: true,
    };

    componentDidMount() {
        Ajax({
            url: 'api/1/project/' + kuura.getProjectId() + '/convert_lat_lon.json',
            data: {
                lat: this.props.vehicle.position.latitude,
                lon: this.props.vehicle.position.longitude,
            },
        }).done(resp => {
            this.setState({
                posE: resp.coords.e.toFixed(2),
                posN: resp.coords.n.toFixed(2),
            });
        });

        VehiclesService.fetchEmissions(this.props.vehicle.uuid).then(emissions => {
            this.setState({
                emissions,
            });
        });

        if (this.isTruck || this.props.isTruckPage) {
            this.statsFilter.setDates(DateUtils.TODAY_START, DateUtils.TODAY_END, 1);
            this.statsFilter.focusVehicle(this.props.vehicle.id, false);
            this.statsStore.fetch();
        }

        this.drawLogpointsPositionTrail();
    }

    componentWillUnmount(): void {
        // clear trail
        const vehiclesLayerWrapper = this.props.mapWrapper.featureLayers.vehicles;
        vehiclesLayerWrapper.clearPositionTrail();

        // remove vehicle logpoints
        if (this.props.clearLogpointsOnClose) {
            this.clearLogpointsLoadedByDialog();
        }
    }

    clearLogpointsLoadedByDialog = () => {
        if (this.isTruck || this.props.isTruckPage) return;

        const loadedLogpoints = this.props.mapWrapper.featureLayers.logpoints.getFeaturesBySource({ vehicleId: this.props.vehicle.id });
        const forRemoval: Feature[] = [];
        loadedLogpoints.forEach(l => {
            if (l.get('loadedByDialog') === true) {
                forRemoval.push(l);
            } else {
                l.set('forceShow', false, true);
            }
        });
        if (forRemoval.length > 0) {
            this.props.mapWrapper.featureLayers.logpoints.removeFeatures(forRemoval);
        }

        this.props.mapWrapper.featureLayers.logpoints.triggerLayerChange();
    };

    setIsAttachmentsLoading = (e: boolean) => {
        this.setState({
            isLoadingAttachments: e,
        });
    };

    render() {
        const v = this.props.vehicle;

        const hasStation = typeof v.position.station !== 'undefined' && v.position.station !== null;
        const hasActiveModel = typeof v.activeModel !== 'undefined' && v.activeModel !== null;
        const hasB = typeof v.position.b !== 'undefined' && v.position.b !== null;

        const stats = this.statsStore.items.filter(i => i.vehicleId == v.id);
        const summary = this.statsStore.groupByTimeAndMaterials(stats, 'all');

        return (
            <div className='vehicle-info-card'>
                <div>
                    <header>{v.name}</header>
                    <div>
                        {__('tablet.driver_js')}: {v.driverName || '/'}, {__('tablet.gsm_js')}: {v.driverPhone || '/'}
                        {this.state.emissions?.hasOwnProperty('powerSource') && (
                            <div>
                                {VehiclesService.powerSources[this.state.emissions?.powerSource]}, {this.state.emissions?.consumption}{' '}
                                {this.state.emissions?.powerSource === 3 ? 'kwh' : 'l/h'}, {this.state.emissions?.weightInTons} tons
                            </div>
                        )}
                    </div>
                </div>

                {window.innerWidth > 1000 && !window.location.href.includes('mobile') && (
                    <a style={{ textDecoration: 'underline' }} href={`vehicles.htm#/overview/${v.id}`} target='_blank'>
                        {__('tablet.vehiclepage_js')}
                    </a>
                )}

                {!this.props.isTruckPage && (
                    <section>
                        <div className='flex-row ai-center'>
                            <strong>{__('tablet.activemodel_js')}: </strong>
                            {hasActiveModel && (
                                <div className='vehicle-info-card__toolbar'>
                                    {this.props.onLoadActiveModel && (
                                        <Button className='btn-simple' icon='iki iki-map' tooltip={kuura.getMessage('js_images.show_on_map')} onClick={this.modelShowOnMap} />
                                    )}
                                    {this.props.onModelShowInTree && (
                                        <Button
                                            className='btn-simple'
                                            icon='iki iki-models'
                                            tooltip={kuura.getMessage('js_map.select_in_files_tree')}
                                            onClick={this.modelShowInTree}
                                        />
                                    )}
                                    {this.props.onModelShowInFilesPage && kuura.hasPrivilege('MODEL_PAGE') && (
                                        <Button
                                            className='btn-simple'
                                            icon='iki iki-file'
                                            tooltip={kuura.getMessage('js_menu.open_in_files_page')}
                                            onClick={this.modelShowInFilesPage}
                                        />
                                    )}
                                    {this.props.onModelShowIn3dPage && (
                                        <Button
                                            className='btn-simple'
                                            icon='iki iki-globe'
                                            tooltip={kuura.getMessage('js_files.view_on_3d_map')}
                                            onClick={this.modelShowIn3dPage}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <span>{hasActiveModel ? v.activeModel?.name : v.activeModelName}</span>

                        {hasStation && (
                            <div>
                                <strong>{__('tablet.station_js')}:</strong> {v.position.station} m
                            </div>
                        )}
                        {hasStation && hasB && (
                            <div>
                                <strong>{__('tablet.sidemeasure_js')}:</strong> {v.position.b} m
                            </div>
                        )}
                    </section>
                )}

                <section>
                    <div>
                        lat: {v.position.latitude}, lon: {v.position.longitude}
                    </div>
                    <div>
                        n: {this.state.posN}, e: {this.state.posE}
                    </div>

                    <div>
                        {__('tablet.position_timestamp_js')}: <DateTimeSpan timestamp={v.position.timestamp} />
                    </div>
                    <div>{v.reportAlert && __('tablet.vehicle_old_report_js')}</div>
                </section>

                <section style={{ marginTop: 15 }} className={cs({ 'overlay-loading': this.state.isLoadingTrailAndLogpoints || this.statsStore.isLoading })}>
                    <strong
                        style={{
                            marginBottom: 5,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <span>
                            {__(this.isTruck || this.props.isTruckPage ? 'js_vehicles.truck_statistics_and_trail_on_map' : 'js_vehicles.logpoints_and_position_trail_on_map')}:
                        </span>
                        &nbsp;
                        {this.state.isTrailTruncated ? (
                            <>
                                <Tooltip target='.trail-truncated-tooltip' baseZIndex={99999} position='top' content={__('js_vehicles.limited_to_n_positions', 10000)} />
                                <i className='iki iki-info trail-truncated-tooltip' style={{ fontSize: '0.97em' }}></i>
                            </>
                        ) : null}
                        <div style={{ marginLeft: 'auto' }} className='flex-row ai-center'>
                            <Tooltip target='.onoffswitch' baseZIndex={99999} position='top' content={__('js_vehicles.show_trail_heatmap')} />
                            <Toggle
                                checked={this.props.mapWrapper.featureLayers.vehicles.store.isHeatmapActive}
                                onClick={() => this.props.mapWrapper.featureLayers.vehicles.changeVisibleLayer()}
                            />
                            {this.props.mapWrapper.featureLayers.vehicles.store.trailOpacity === 0 && <i className='iki iki-hide' style={{ fontSize: '1em', marginLeft: '8px' }} />}
                            <input
                                type='range'
                                min={0}
                                max={100}
                                step={10}
                                value={this.props.mapWrapper.featureLayers.vehicles.store.trailOpacity}
                                style={{ width: 60, transform: 'scale(0.8)' }}
                                onChange={e => this.props.mapWrapper.featureLayers.vehicles.setTrailOpacity(parseInt(e.currentTarget.value))}
                                title={__('js_vehicles.trail_opacity')}
                            />
                        </div>
                    </strong>
                    <DateRangePicker
                        store={this.state}
                        clearable={true}
                        onChange={(from, to, presetIndex) => {
                            this.setState(
                                {
                                    fromTimestamp: from,
                                    toTimestamp: to,
                                    datePresetIndex: presetIndex,
                                },
                                this.drawLogpointsPositionTrail
                            );

                            this.statsFilter.setDates(from, to, presetIndex);
                            this.statsStore.fetch();
                        }}
                    />

                    {(this.isTruck || this.props.isTruckPage) && <MaterialStatsTable stats={summary} groupedBy={'all'} />}

                    {this.state.loadedLogpoints != -1 && (
                        <div className='flex-row ai-center' style={{ marginTop: 5 }}>
                            <span>
                                {__('js_map.number_of_logpoints')}: {this.state.loadedLogpoints}
                            </span>

                            {this.state.loadedLogpoints > 0 && (
                                <Button
                                    style={{ marginLeft: 5 }}
                                    className='ik-button-secondary--subtle ik-button-small'
                                    icon='iki iki-fit-to-screen'
                                    tooltip={__('js_map.fit_into_view')}
                                    onClick={this.onClickFitView}
                                />
                            )}
                        </div>
                    )}
                </section>

                {v.uuid && !this.props.isTruckPage && (
                    <section className={cs({ 'overlay-loading': this.state.isLoadingAttachments })}>
                        <strong>{__('js_visualization.attached_documents_and_links')}: </strong>
                        <AttachDocumentsAndLinks
                            modelUuid={v.uuid}
                            getAttachments={(equipmentUuid: string) => {
                                this.setIsAttachmentsLoading(true);
                                return EquipmentAttachmentsService.getAttachments(equipmentUuid).done(() => this.setIsAttachmentsLoading(false));
                            }}
                            addNewAttachment={(equipmentUuid: string, data: IAttachmentData) => {
                                this.setIsAttachmentsLoading(true);
                                return EquipmentAttachmentsService.addNewAttachment(equipmentUuid, data).done(() => this.setIsAttachmentsLoading(false));
                            }}
                            deleteAttachment={(attachmentId: string, equipmentUuid: string) => {
                                this.setIsAttachmentsLoading(true);
                                return EquipmentAttachmentsService.deleteAttachment(attachmentId, equipmentUuid).done(() => this.setIsAttachmentsLoading(false));
                            }}
                            maxHeight='initial'
                            treeData={this.props.treeData}
                        />
                    </section>
                )}
            </div>
        );
    }

    modelShowOnMap = () => {
        if (typeof this.props.onLoadActiveModel === 'function') {
            this.props.onLoadActiveModel(this.props.vehicle.activeModel.id);
        }
    };

    modelShowInTree = () => {
        if (typeof this.props.onModelShowInTree === 'function') {
            this.props.onModelShowInTree(this.props.vehicle.activeModel.id);
        }
    };

    modelShowInFilesPage = () => {
        if (typeof this.props.onModelShowInFilesPage === 'function') {
            this.props.onModelShowInFilesPage(this.props.vehicle.activeModel.id);
        }
    };

    modelShowIn3dPage = () => {
        if (typeof this.props.onModelShowIn3dPage === 'function') {
            this.props.onModelShowIn3dPage(this.props.vehicle.activeModel.id);
        }
    };

    drawLogpointsPositionTrail = () => {
        this.setState({
            isLoadingTrailAndLogpoints: true,
        });

        Promise.all([
            this.props.mapWrapper.featureLayers.vehicles.drawPositionTrail(this.props.vehicle.id, this.state.fromTimestamp, this.state.toTimestamp).always(resp => {
                this.setState({
                    isTrailTruncated: resp.resultTruncated,
                });
            }),
            this.loadLogpoints(),
        ]).finally(() => {
            this.setState({
                isLoadingTrailAndLogpoints: false,
            });
        });
    };

    loadLogpoints = () => {
        if (this.isTruck || this.props.isTruckPage) return;

        const vehicleId = this.props.vehicle.id;
        this.clearLogpointsLoadedByDialog();

        return Ajax({
            url: 'ajax_vehicle_logpoints.json',
            data: {
                vehicleId,
                sinceUnixTimeMillis: this.state.fromTimestamp,
                untilUnixTimeMillis: this.state.toTimestamp,
            },
        }).done(resp => {
            for (let i = 0, ilen = resp.logpoints.length; i < ilen; i++) {
                resp.logpoints[i].vehicleId = vehicleId;
            }
            const allIds = resp.logpoints.map(l => l.id);
            const alreadyLoadedIds = this.props.mapWrapper.featureLayers.logpoints.getFeaturesBySource({ vehicleId: vehicleId }).map(l => l.get('data').id);

            // draw, but only some are new, some might have already been loaded
            this.props.mapWrapper.featureLayers.logpoints.drawAll(resp.logpoints);

            const loadedLogpointsFeatures = [];
            this.props.mapWrapper.featureLayers.logpoints.getFeaturesBySource({ vehicleId: vehicleId }).forEach(l => {
                const id = l.get('data').id;
                if (allIds.includes(id)) {
                    loadedLogpointsFeatures.push(l);
                    // important to have l.set be silent here, and trigger rerender when loop is done
                    l.set('forceShow', true, true); // dont allow filters to hide these logpoints

                    if (!alreadyLoadedIds.includes(id)) {
                        l.set('loadedByDialog', true, true);
                    }
                }
            });
            this.props.mapWrapper.featureLayers.logpoints.triggerLayerChange();

            this.setState({
                loadedLogpoints: resp.logpoints.length,
                loadedLogpointsFeatures,
            });

            if (this.props.onLogpointsLoaded && resp.logpoints.length) {
                this.props.onLogpointsLoaded(resp.logpoints.map(l => l.id));
            }
        });
    };

    onClickFitView = () => {
        this.props.mapWrapper.fitFeatures(this.state.loadedLogpointsFeatures);
    };

    get isTruck(): boolean {
        return this.props.vehicle.type === 6 || this.props.vehicle.type === 9;
    }
}
